import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { format, addMonths, subMonths, addYears, subYears, getDaysInMonth } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ChevronLeft, ChevronRight, RefreshCw, CalendarDays, Download, Printer, Loader2, ListChecks } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
  return isFinite(n) ? n : 0;
};
const RE_BEER_T = /\(\s*beer\s*t\)\s*$/i;
const RE_STATE_T = /\(\s*t\)\s*$/i;
const RE_PAID_OUT = /paid\s*-?\s*out/i;
const RE_MONEY_ORDER = /^money\s*orders?$/i;
const RE_LOTTERY_ROW = /lottery|^\$?\d+\s*instant\b/i;
const RE_FIRST_DATA = /first\s*data/i;
const RE_EBT = /\bebt\b/i;
const RE_GAS = /gas|fuel/i;
const RE_SKILL = /skill/i;

// Per-day metric extraction from a store_paperwork data doc (+ merged l_* lottery autos).
// rowLcs: extra lowercase row names (from the Item Tracker queue) summed as `row:{name}` keys.
const computeDay = (data, rowLcs) => {
  const debit = data.debit_rows || [];
  const credit = data.credit_rows || [];
  const suppliers = data.supplier_rows || [];
  const cashPO = data.cash_paid_out || [];
  const fuel = data.fuel_rows || [];
  const all = [...debit, ...credit];

  let taxable = 0, inside = 0, salesTax = 0, debitTotal = 0, creditTotal = 0;
  for (const r of debit) {
    if (!r) continue;
    debitTotal += num(r.amount);
    if (r._kind === "state_tax_auto" || r._kind === "beer_tax_auto") { salesTax += num(r.amount); continue; }
    if (r._kind) continue;
    const name = (r.name || "").trim();
    if (!name) continue;
    if (RE_BEER_T.test(name) || RE_STATE_T.test(name)) { taxable += num(r.amount); inside += num(r.amount); continue; }
    if (RE_LOTTERY_ROW.test(name) || RE_MONEY_ORDER.test(name) || RE_PAID_OUT.test(name)) continue;
    inside += num(r.amount);
  }
  for (const r of credit) creditTotal += num(r?.amount);

  const sumBy = (rows, test) => rows.reduce((s, r) => (r && !r._kind && test((r.name || "").trim()) ? s + num(r.amount) : s), 0);

  const hasLotteryAuto = ["l_instant_sale", "l_online_sale", "l_instant_paid_out", "l_online_paid_out"].some((k) => data[k] != null);
  const lotterySale = hasLotteryAuto
    ? num(data.l_instant_sale) + num(data.l_online_sale)
    : sumBy(debit, (n) => RE_LOTTERY_ROW.test(n) && !RE_PAID_OUT.test(n));
  const lotteryPaidOut = hasLotteryAuto
    ? num(data.l_instant_paid_out) + num(data.l_online_paid_out)
    : sumBy(all, (n) => /(instant|online)\s+paid/i.test(n));

  const out = {
    taxable,
    nonTaxable: inside - taxable,
    totalSale: inside,
    salesTax,
    firstData: sumBy(all, (n) => RE_FIRST_DATA.test(n) && !RE_EBT.test(n)),
    ebt: sumBy(all, (n) => RE_EBT.test(n)),
    moneyOrders: sumBy(all, (n) => RE_MONEY_ORDER.test(n)),
    lotterySale,
    lotteryPaidOut,
    gasGallon: fuel.reduce((s, r) => s + num(r?.sold_volume), 0),
    gasSale: fuel.reduce((s, r) => s + num(r?.sold_amount), 0),
    gasPurchase: [...suppliers, ...cashPO].reduce((s, r) => (r && RE_GAS.test(r.name || "") ? s + num(r.amount) : s), 0),
    bankDeposit: sumBy(credit, (n) => /^total\s+(cash|checks)$/i.test(n)),
    skillPO: [...all, ...cashPO, ...suppliers].reduce((s, r) => (r && !r._kind && RE_SKILL.test(r.name || "") ? s + num(r.amount) : s), 0),
    overShort: creditTotal - debitTotal,
  };
  if (rowLcs && rowLcs.size) {
    for (const lc of rowLcs) out[`row:${lc}`] = 0;
    for (const r of all) {
      if (!r) continue;
      const lc = (r.name || "").trim().toLowerCase();
      if (lc && rowLcs.has(lc)) out[`row:${lc}`] += num(r.amount);
    }
  }
  return out;
};

const ITEMS = [
  { key: "taxable", label: "Taxable" },
  { key: "nonTaxable", label: "Non Taxable" },
  { key: "totalSale", label: "Total Sale" },
  { key: "salesTax", label: "Sales Tax" },
  { key: "firstData", label: "First Data" },
  { key: "ebt", label: "EBT" },
  { key: "moneyOrders", label: "Money Orders" },
  { key: "lotterySale", label: "Lottery Sale" },
  { key: "lotteryPaidOut", label: "Lottery Paid Out" },
  { key: "gasGallon", label: "Gasoline Gallon", gallons: true },
  { key: "gasSale", label: "Gasoline Sale" },
  { key: "gasPurchase", label: "Gasoline Purchase" },
  { key: "bankDeposit", label: "Bank Deposit" },
  { key: "skillPO", label: "Skill P/O" },
  { key: "overShort", label: "Over/Short", signed: true },
];

// Queue names that map onto computed metrics (incl. Item Tracker "computed" entries)
const METRIC_LOOKUP = (() => {
  const m = {};
  ITEMS.forEach((it) => { m[it.label.toLowerCase()] = it; });
  m["store inside sale"] = ITEMS.find((i) => i.key === "totalSale");
  m["fuel gallons"] = ITEMS.find((i) => i.key === "gasGallon");
  m["total lottery sale (instant + online)"] = ITEMS.find((i) => i.key === "lotterySale");
  m["total tax (beer + state)"] = ITEMS.find((i) => i.key === "salesTax");
  return m;
})();

// Day cells: rounded whole numbers keep the sheet compact / one page.
const fmtCell = (v, item) => {
  const n = Number(v) || 0;
  if (Math.abs(n) < 0.005) return "";
  const r = Math.round(Math.abs(n)).toLocaleString("en-US");
  return item?.signed && n < 0 ? `(${r})` : r;
};
const fmtSum = (v, item, showZero = true) => {
  const n = Number(v) || 0;
  if (!showZero && Math.abs(n) < 0.005) return "";
  const digits = item?.gallons ? 1 : 2;
  const abs = Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return item?.signed && n < -0.004 ? `(${abs})` : abs;
};

export default function MonthlyDataSheet() {
  const { user } = useAuth();
  const [view, setView] = useState("month");
  const [monthDate, setMonthDate] = useState(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); });
  const [days, setDays] = useState([]);
  const [prevDays, setPrevDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Item columns follow the Item Tracker queue (shared via localStorage)
  const [queue, setQueue] = useState(() => {
    try {
      const q = JSON.parse(localStorage.getItem("item_tracker_queue") || "[]");
      return Array.isArray(q) ? q : [];
    } catch { return []; }
  });
  useEffect(() => {
    const sync = () => {
      try {
        const q = JSON.parse(localStorage.getItem("item_tracker_queue") || "[]");
        setQueue(Array.isArray(q) ? q : []);
      } catch { /* ignore */ }
    };
    window.addEventListener("item-tracker-queue-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("item-tracker-queue-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const activeItems = useMemo(() => {
    const names = queue.map((n) => String(n || "").trim()).filter(Boolean);
    if (!names.length) return ITEMS;
    const seenKeys = new Set();
    const out = [];
    names.forEach((name) => {
      const lc = name.toLowerCase();
      const metric = METRIC_LOOKUP[lc];
      const item = metric ? { ...metric, label: name } : { key: `row:${lc}`, label: name, row: true };
      if (seenKeys.has(item.key)) return;
      seenKeys.add(item.key);
      out.push(item);
    });
    return out.length ? out : ITEMS;
  }, [queue]);

  const monthStr = format(monthDate, "yyyy-MM");
  const yearStr = format(monthDate, "yyyy");
  const prevMonthDate = subMonths(monthDate, 1);
  const periodLabel = view === "year" ? yearStr : format(monthDate, "MMMM yyyy");
  const sheetTitle = view === "year" ? "Year Overview" : "Monthly Data Sheet";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "year") {
        const res = await axios.get(`${API}/store-paperwork/year/${yearStr}`);
        setDays(res.data?.days || []);
        setPrevDays([]);
      } else {
        const prevStr = format(subMonths(new Date(monthStr + "-15"), 1), "yyyy-MM");
        const [cur, prev] = await Promise.all([
          axios.get(`${API}/store-paperwork/month/${monthStr}`),
          axios.get(`${API}/store-paperwork/month/${prevStr}`).catch(() => ({ data: { days: [] } })),
        ]);
        setDays(cur.data?.days || []);
        setPrevDays(prev.data?.days || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [monthStr, yearStr, view]);

  useEffect(() => { load(); }, [load]);

  // periods = table ROWS (dates in month view, months in year view)
  const { periods, totals, grand, prevTotals, prevGrand } = useMemo(() => {
    const rowLcs = new Set(activeItems.filter((i) => i.row).map((i) => i.key.slice(4)));
    let periods;
    if (view === "year") {
      const per = Array.from({ length: 12 }, () => null);
      const counts = Array(12).fill(0);
      days.forEach((d) => {
        const mi = parseInt(String(d.date).slice(5, 7), 10) - 1;
        if (mi < 0 || mi > 11) return;
        const m = computeDay(d.data || {}, rowLcs);
        if (!activeItems.some((it) => Math.abs(m[it.key]) > 0.004)) return;
        if (!per[mi]) per[mi] = Object.fromEntries(activeItems.map((it) => [it.key, 0]));
        activeItems.forEach((it) => { per[mi][it.key] += m[it.key]; });
        counts[mi]++;
      });
      periods = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((lab, i) => ({
        iso: `${format(monthDate, "yyyy")}-${String(i + 1).padStart(2, "0")}`,
        dateLabel: lab,
        dayLabel: counts[i] ? `${counts[i]}d` : "",
        weekend: false,
        m: per[i],
        hasData: !!per[i] && activeItems.some((it) => Math.abs(per[i][it.key]) > 0.004),
      }));
    } else {
      const byDate = {};
      days.forEach((d) => { byDate[d.date] = computeDay(d.data || {}, rowLcs); });
      const n = getDaysInMonth(monthDate);
      periods = [];
      for (let i = 1; i <= n; i++) {
        const d = new Date(monthDate.getFullYear(), monthDate.getMonth(), i);
        const iso = format(d, "yyyy-MM-dd");
        const m = byDate[iso] || null;
        const hasData = m && activeItems.some((c) => Math.abs(m[c.key]) > 0.004);
        periods.push({ iso, dateLabel: format(d, "M/d"), dayLabel: format(d, "EEE"), weekend: [0, 6].includes(d.getDay()), m, hasData });
      }
    }
    const totals = {};
    const withData = periods.filter((c) => c.hasData);
    activeItems.forEach((it) => {
      totals[it.key] = withData.reduce((s, c) => s + c.m[it.key], 0);
    });
    // per-period grand = sum of SELECTED items (gallons excluded — volume, not $)
    const moneyItems = activeItems.filter((it) => !it.gallons);
    const grand = {
      perRow: periods.map((c) => (c.hasData ? moneyItems.reduce((s, it) => s + c.m[it.key], 0) : null)),
      total: moneyItems.reduce((s, it) => s + (totals[it.key] || 0), 0),
    };
    // month trends: compare this month's per-item totals vs last month
    let prevTotals = null, prevGrand = null;
    if (view === "month" && prevDays.length) {
      const sums = Object.fromEntries(activeItems.map((it) => [it.key, 0]));
      let any = false;
      prevDays.forEach((d) => {
        const m = computeDay(d.data || {}, rowLcs);
        if (!activeItems.some((it) => Math.abs(m[it.key]) > 0.004)) return;
        any = true;
        activeItems.forEach((it) => { sums[it.key] += m[it.key]; });
      });
      if (any) {
        prevTotals = sums;
        prevGrand = moneyItems.reduce((s, it) => s + sums[it.key], 0);
      }
    }
    return { periods, totals, grand, prevTotals, prevGrand };
  }, [days, prevDays, monthDate, view, activeItems]);

  const anyData = periods.some((c) => c.hasData);

  // small ▲/▼ vs last month, with tooltip showing previous total and % change
  const trendArrow = (cur, prev, item, testId) => {
    if (prev == null) return null;
    const c = Number(cur) || 0, p = Number(prev) || 0;
    if (Math.abs(p) < 0.005 && Math.abs(c) < 0.005) return null;
    const d = c - p;
    if (Math.abs(d) < 0.005) return null;
    const up = d > 0;
    const pct = Math.abs(p) > 0.005 ? (d / Math.abs(p)) * 100 : null;
    const tip = `vs ${format(prevMonthDate, "MMM yyyy")}: ${fmtSum(p, item)}${pct != null ? ` (${up ? "+" : ""}${pct.toFixed(1)}%)` : ""}`;
    return (
      <span
        className={`ml-0.5 text-[8px] align-middle cursor-help ${up ? "text-emerald-400" : "text-rose-400"}`}
        title={tip}
        data-testid={testId}
      >
        {up ? "▲" : "▼"}
      </span>
    );
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const payload = {
        month: view === "year" ? yearStr : monthStr,
        month_label: periodLabel,
        sheet_title: sheetTitle,
        orientation: activeItems.length > 9 ? "landscape" : "portrait",
        days: periods.map((c) => ({ num: c.dateLabel, dow: c.dayLabel })),
        items: activeItems.map((it) => ({
          label: it.label,
          gallons: !!it.gallons,
          cells: periods.map((c) => (c.m ? c.m[it.key] : null)),
          total: totals[it.key] || 0,
        })),
      };
      const res = await axios.post(`${API}/export/monthly-data-sheet`, payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sheetTitle.replace(/\s+/g, "")}_${view === "year" ? yearStr : monthStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${sheetTitle} (${periodLabel})`);
    } catch {
      toast.error("Excel export failed");
    } finally {
      setExporting(false);
    }
  };

  const printSheet = () => {
    const title = `${sheetTitle} — ${periodLabel}`;
    const landscape = activeItems.length > 9; // many item columns → landscape page
    const store = (user?.store_name || "").trim();
    const th = activeItems.map((it) => `<th class="ith">${it.label}</th>`).join("");
    const rowsHtml = periods.map((c, i) => {
      const tds = activeItems.map((it) => {
        const v = c.m ? c.m[it.key] : 0;
        const neg = it.signed && v < -0.004 ? " neg" : "";
        return `<td class="${neg}">${c.m ? fmtCell(v, it) : ""}</td>`;
      }).join("");
      const g = grand.perRow[i];
      return `<tr class="${c.weekend ? "we" : i % 2 ? "alt" : ""}"><td class="lbl">${c.dateLabel}</td><td class="day">${c.dayLabel}</td>${tds}<td class="sum${g != null && g < -0.004 ? " neg" : ""}">${g != null ? fmtCell(g, { signed: true }) : ""}</td></tr>`;
    }).join("");
    const totalTds = activeItems.map((it) => {
      const tNeg = it.signed && totals[it.key] < -0.004 ? " neg" : "";
      return `<td class="gt${tNeg}">${anyData ? fmtSum(totals[it.key], it) : ""}</td>`;
    }).join("");
    const totalRow = `<tr><td class="lbl gt">Total</td><td class="day gt"></td>${totalTds}<td class="sum gt${grand.total < -0.004 ? " neg" : ""}">${anyData ? fmtSum(grand.total, { signed: true }) : ""}</td></tr>`;
    const html = `<!doctype html><html><head><title>${title}</title><style>
      @page { size: ${landscape ? "landscape" : "portrait"}; margin: 8mm; }
      html, body { margin: 0; padding: 0; }
      body { font-family: Consolas, "Courier New", monospace; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      #sheet { display: inline-block; }
      h1 { font-size: 13px; margin: 0 0 6px; white-space: nowrap; }
      table { border-collapse: collapse; }
      tr, thead, tfoot { page-break-inside: avoid; }
      th, td { border: 1px solid #999; padding: 1px 2px; font-size: 7.5px; text-align: right; white-space: nowrap; }
      th { background: #e2ede2; vertical-align: bottom; }
      .ith { white-space: normal; word-break: break-word; max-width: 44px; min-width: 30px; text-align: center; font-size: 7px; line-height: 1.1; }
      .lbl { text-align: left; font-weight: bold; white-space: nowrap; background: #f0f4f0; }
      .day { text-align: center; color: #555; background: #f7faf7; }
      .we td { background: #efefef; } .alt td { background: #fafafa; }
      .sum { font-weight: bold; border-left: 2px solid #333; background: #f6ecd2; }
      .gt { font-weight: bold; background: #e8e2cd; border-top: 2px solid #333; }
      .neg { color: #b91c1c; font-style: italic; }
    </style></head><body><div id="sheet"><h1>${store ? store + " — " : ""}${title}</h1><table><thead><tr><th class="lbl">Date</th><th class="day">Day</th>${th}<th class="sum">Total</th></tr></thead><tbody>${rowsHtml}</tbody><tfoot>${totalRow}</tfoot></table></div></body></html>`;
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:1100px;height:800px;border:0;visibility:hidden;pointer-events:none;";
    document.body.appendChild(iframe);
    iframe.contentDocument.open();
    iframe.contentDocument.write(html);
    iframe.contentDocument.close();
    setTimeout(() => {
      // scale-to-fit: shrink (or gently grow) the sheet so everything prints on ONE page
      const doc = iframe.contentDocument;
      const sheet = doc.getElementById("sheet");
      const MM = 96 / 25.4; // mm → CSS px
      // usable area = min(A4, Letter) minus 8mm margins each side, per orientation
      const availW = ((landscape ? 279.4 : 210) - 16) * MM;
      const availH = ((landscape ? 210 : 279.4) - 16) * MM;
      const w = sheet.offsetWidth || 1;
      const h = sheet.offsetHeight || 1;
      const scale = Math.min(availW / w, availH / h, 1.5);
      sheet.style.zoom = String(scale);
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => iframe.remove(), 3000);
    }, 150);
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden" data-testid="mds-section">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b border-border/60">
        <CalendarDays className="w-4 h-4 text-primary" />
        <h4 className="font-mono text-sm font-bold mr-1">Monthly Data Sheet</h4>
        <div className="flex rounded-lg overflow-hidden border border-border mr-1">
          <button
            onClick={() => setView("month")}
            className={`px-2.5 py-1 font-mono text-[11px] font-bold ${view === "month" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="mds-view-month"
          >
            Month
          </button>
          <button
            onClick={() => setView("year")}
            className={`px-2.5 py-1 font-mono text-[11px] font-bold ${view === "year" ? "bg-primary text-primary-foreground" : "bg-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid="mds-view-year"
          >
            Year
          </button>
        </div>
        <Button variant="outline" size="icon" className="h-8 w-8 btn-3d" onClick={() => setMonthDate((d) => (view === "year" ? subYears(d, 1) : subMonths(d, 1)))} data-testid="mds-prev-month">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="font-mono text-sm font-bold px-2 min-w-[130px] text-center" data-testid="mds-month-label">
          {periodLabel}
        </span>
        <Button variant="outline" size="icon" className="h-8 w-8 btn-3d" onClick={() => setMonthDate((d) => (view === "year" ? addYears(d, 1) : addMonths(d, 1)))} data-testid="mds-next-month">
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" className="h-8 btn-3d" onClick={load} disabled={loading} data-testid="mds-reload">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
          <span className="text-xs">Reload</span>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 btn-3d" data-testid="mds-items-btn">
              <ListChecks className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">Items ({activeItems.length}){queue.length === 0 ? " · defaults" : ""}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Sheet items</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 font-mono text-[11px] text-muted-foreground leading-relaxed" data-testid="mds-items-info">
              Columns follow the <b className="text-foreground">Item Tracker</b> queue below — add or remove items there (any department, tender or computed total).
              {queue.length === 0 && <span className="block mt-1 text-amber-500">Queue is empty — showing the default item set.</span>}
            </div>
            {queue.length > 0 && (
              <>
                <DropdownMenuSeparator />
                {activeItems.map((it) => (
                  <div key={it.key} className="px-2 py-1 font-mono text-xs flex items-center gap-1.5" data-testid={`mds-item-${it.key}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${it.row ? "bg-sky-400" : "bg-emerald-400"}`} />
                    {it.label}
                  </div>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" className="h-8 btn-3d" onClick={printSheet} disabled={loading || activeItems.length === 0} title="Print the sheet" data-testid="mds-print-btn">
          <Printer className="w-3.5 h-3.5 mr-1" />
          <span className="text-xs">Print</span>
        </Button>
        <Button variant="outline" size="sm" className="h-8 btn-3d" onClick={exportExcel} disabled={loading || exporting || activeItems.length === 0} title="Download as Excel (.xlsx)" data-testid="mds-excel-btn">
          {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
          <span className="text-xs">{exporting ? "Exporting…" : "Excel"}</span>
        </Button>
        <span className="font-mono text-[10px] text-muted-foreground ml-auto">
          Items from Item Tracker queue · whole $ cells · Total row exact
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full font-mono text-[8px] leading-tight tabular-nums border-collapse" data-testid="mds-table">
          <thead>
            <tr className="bg-emerald-900/60 text-emerald-100">
              <th className="px-1.5 py-1 text-left whitespace-nowrap sticky left-0 bg-emerald-900 z-10 text-[9px]">Date</th>
              <th className="px-1 py-1 text-center text-[8px]">Day</th>
              {activeItems.map((it) => (
                <th key={it.key} className="px-1 py-1 text-center align-bottom whitespace-normal break-words leading-tight max-w-[58px] text-[8px]" data-testid={`mds-col-${it.key}`}>
                  {it.label}
                </th>
              ))}
              <th className="px-1.5 py-1 text-right text-[9px] bg-emerald-950 border-l-2 border-primary/50">Total</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((c, i) => (
              <tr key={c.iso} className={`border-b border-border/30 ${c.weekend ? "bg-emerald-900/20" : i % 2 ? "bg-muted/20" : ""} ${!c.hasData ? "opacity-45" : ""}`} data-testid={`mds-row-${c.iso}`}>
                <td className="px-1.5 py-px whitespace-nowrap sticky left-0 bg-card z-10 font-bold text-[9px]">{c.dateLabel}</td>
                <td className="px-1 py-px text-center text-emerald-300/80">{c.dayLabel}</td>
                {activeItems.map((it) => (
                  <td key={it.key} className={`px-1 py-px text-right ${it.signed && c.m && c.m[it.key] < -0.004 ? "text-rose-300 italic" : ""}`}>
                    {c.m ? fmtCell(c.m[it.key], it) : ""}
                  </td>
                ))}
                <td className={`px-1.5 py-px text-right font-bold bg-emerald-950/50 border-l-2 border-primary/50 ${grand.perRow[i] != null && grand.perRow[i] < -0.004 ? "text-rose-300 italic" : ""}`}>
                  {grand.perRow[i] != null ? fmtCell(grand.perRow[i], { signed: true }) : ""}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-emerald-900/60 border-t-2 border-primary/60" data-testid="mds-total-row">
              <td className="px-1.5 py-1 whitespace-nowrap sticky left-0 bg-emerald-900 z-10 font-bold text-[9px]">Total</td>
              <td className="px-1 py-1" />
              {activeItems.map((it) => (
                <td key={it.key} className={`px-1 py-1 text-right font-bold whitespace-nowrap ${it.signed && totals[it.key] < -0.004 ? "text-rose-300 italic" : ""}`}>
                  {anyData ? fmtSum(totals[it.key], it) : ""}
                  {anyData && prevTotals ? trendArrow(totals[it.key], prevTotals[it.key], it, `mds-trend-${it.key}`) : null}
                </td>
              ))}
              <td className={`px-1.5 py-1 text-right font-bold text-[9px] bg-emerald-950 border-l-2 border-primary/50 whitespace-nowrap ${grand.total < -0.004 ? "text-rose-300 italic" : ""}`}>
                {anyData ? fmtSum(grand.total, { signed: true }) : ""}
                {anyData && prevGrand != null ? trendArrow(grand.total, prevGrand, { signed: true }, "mds-trend-grand") : null}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {!anyData && !loading && (
        <p className="font-mono text-xs text-muted-foreground p-3" data-testid="mds-empty">No paperwork data found for {periodLabel}.</p>
      )}
    </div>
  );
}
